import { CoachCategoryEntity } from "@modules/coachCategories/entities/coachCategoryEntity"
import { coachCategoriesKeys } from "@modules/coachCategories/gateway/coachCategoriesKeys"
import type {
  CategoryProgramBodyInputDto,
  CategorySlotDefaultInputDto,
  CategorySlotDefaultValueDto,
} from "@modules/coachCategories/gateway/coachCategoriesDto"
import { useServicesContext } from "@modules/core/services/services.context"
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"

export function useCoachCategories() {
  const { coachCategoriesApi } = useServicesContext()

  const { data } = useSuspenseQuery<CoachCategoryEntity[]>({
    queryKey: coachCategoriesKeys.list(),
    queryFn: async () => {
      const result = await coachCategoriesApi.getCoachCategories()
      return result.map((dto) => CoachCategoryEntity.fromDto(dto))
    },
  })

  return { data }
}

export function useCoachCategorySlotDefaults(id: string) {
  const { coachCategoriesApi } = useServicesContext()

  const { data } = useSuspenseQuery<CategorySlotDefaultValueDto>({
    queryKey: coachCategoriesKeys.slotDefaults(id),
    queryFn: () => coachCategoriesApi.getCoachCategorySlotDefaults(id),
  })

  return { data }
}

export type CoachCategorySaveInput = {
  program: CategoryProgramBodyInputDto
  slotDefaults: CategorySlotDefaultInputDto
}

export function useCreateCoachCategory() {
  const { coachCategoriesApi } = useServicesContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ program, slotDefaults }: CoachCategorySaveInput) => {
      const createdCategory = await coachCategoriesApi.createCoachCategory(program)
      await coachCategoriesApi.updateCoachCategorySlotDefaults(createdCategory.id, slotDefaults)
      return CoachCategoryEntity.fromDto(createdCategory)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: coachCategoriesKeys.list() })
    },
  })
}

export function useUpdateCoachCategory(id: string) {
  const { coachCategoriesApi } = useServicesContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ program, slotDefaults }: CoachCategorySaveInput) => {
      const updatedCategory = await coachCategoriesApi.updateCoachCategory(id, program)
      await coachCategoriesApi.updateCoachCategorySlotDefaults(id, slotDefaults)
      return CoachCategoryEntity.fromDto(updatedCategory)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: coachCategoriesKeys.list() })
      await queryClient.invalidateQueries({ queryKey: coachCategoriesKeys.detail(id) })
      await queryClient.invalidateQueries({ queryKey: coachCategoriesKeys.slotDefaults(id) })
    },
  })
}

export function useDeleteCoachCategory() {
  const { coachCategoriesApi } = useServicesContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      await coachCategoriesApi.deleteCoachCategory(id)
    },
    onSuccess: async (_data, id) => {
      await queryClient.invalidateQueries({ queryKey: coachCategoriesKeys.list() })
      queryClient.removeQueries({ queryKey: coachCategoriesKeys.detail(id) })
    },
  })
}
